import React, { useState, useEffect } from "react";
import RoleServiceInstance from "service/role.service";
import RoleTable from "./components/RoleTable";
import RoleFormModal from "./components/RoleFormModal";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import ActionBar from "components/action-bar";
import HeaderSection from "components/headers-section";

const RoleManager = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState({
    role_name: "",
    role_description: "",
    privilege: 4, // Valeur par défaut pour le niveau de privilège le plus bas
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await RoleServiceInstance.getAll();
      setRoles(result || []);
    } catch (error) {
      setError(
        "Impossible de charger les rôles. Veuillez réessayer plus tard."
      );
      console.error("Error fetching roles:", error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setCurrentRole({
      role_name: "",
      role_description: "",
      privilege: getNextAvailablePrivilege(),
    });
    setIsEditing(false);
    setIsFormModalOpen(true);
  };

  const openEditModal = (role) => {
    setCurrentRole({
      role_id: role.role_id,
      role_name: role.role_name,
      role_description: role.role_description,
      privilege: role.privilege,
    });
    setIsEditing(true);
    setIsFormModalOpen(true);
  };

  const openDeleteModal = (role) => {
    setCurrentRole(role);
    setIsDeleteModalOpen(true);
  };

  // Obtenir le prochain niveau de privilège disponible
  const getNextAvailablePrivilege = () => {
    if (roles.length === 0) return 1;
    const privileges = roles.map((role) => role.privilege);
    return Math.max(...privileges) + 1;
  };

  const handleSubmitForm = async (formData) => {
    setLoading(true);
    try {
      if (isEditing) {
        await RoleServiceInstance.update(currentRole.role_id, formData);
      } else {
        await RoleServiceInstance.create(formData);
      }

      fetchRoles();
      setIsFormModalOpen(false);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        errors: error.response?.data?.errors || {
          general: isEditing
            ? "Impossible de modifier le rôle. Veuillez réessayer."
            : "Impossible de créer le rôle. Veuillez réessayer.",
        },
      };
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await RoleServiceInstance.delete(currentRole.role_id);
      fetchRoles();
      setIsDeleteModalOpen(false);
    } catch (error) {
      setError("Impossible de supprimer le rôle. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = roles.filter(
    (role) =>
      role.role_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.role_description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Trier les rôles par niveau de privilège (du plus élevé au plus bas)
  const sortedRoles = [...filteredRoles].sort(
    (a, b) => a.privilege - b.privilege
  );

  return (
    <div className="min-h-screen bg-gray-50 px-5 py-8 md:px-8">
      <HeaderSection
        title="Gestion des Rôles"
        description="Définissez et gérez les rôles et leurs privilèges"
        accentColor="#4B5563" // Couleur gris foncé pour les rôle
      />

      <ActionBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onRefresh={fetchRoles}
        onCreateNew={openCreateModal}
        buttonLabel="Nouveau"
        loading={loading}
      />

      {error && (
        <div
          className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600"
          role="alert"
        >
          {error}
        </div>
      )}

      <RoleTable
        roles={sortedRoles}
        loading={loading}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      <RoleFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleSubmitForm}
        initialRole={currentRole}
        isEditing={isEditing}
        existingRoles={roles}
        loading={loading}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        item={currentRole}
        itemName={currentRole.role_name}
        itemType="rôle"
        warning="La suppression de ce rôle peut affecter les utilisateurs qui y sont associés."
        loading={loading}
      />
    </div>
  );
};

export default RoleManager;
